"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Dialog } from "@base-ui/react/dialog";
import { Menu, X, Sun, Moon, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV_GROUPS, isActive } from "@/lib/nav";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { useConnectorHealth } from "@/hooks/queries";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserLike {
  email?: string;
  user_metadata?: { full_name?: string };
}

function asUserLike(user: unknown): UserLike | null {
  return (user ?? null) as UserLike | null;
}

function getUserInitials(user: UserLike | null): string {
  if (!user) return "?";
  const name = user.user_metadata?.full_name;
  if (name) {
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (user.email?.[0] ?? "?").toUpperCase();
}

function getDisplayName(user: UserLike | null): string {
  return user?.user_metadata?.full_name ?? "User";
}

/* ─── status dot ─── */

const STATE_STYLES: Record<
  "healthy" | "degraded" | "stale" | "unknown",
  {
    dot: string;
    label: (total: number, degraded: number) => string;
    tooltip: string;
  }
> = {
  healthy: {
    dot: "bg-emerald-400",
    label: (total) => `Connectors: ${total} active`,
    tooltip:
      "All data sources synced recently. Discovery results reflect the latest available signals.",
  },
  degraded: {
    dot: "bg-amber-400",
    label: (total, degraded) =>
      `Connectors: ${total} active (${degraded} degraded)`,
    tooltip:
      "Some data sources are slow or returning errors. The backend retries automatically; results may be partial until they recover.",
  },
  stale: {
    dot: "bg-red-400",
    label: (total) => `Connectors: ${total} active (stale)`,
    tooltip:
      "Some data sources haven't refreshed in over 24h. The backend retries automatically — no action needed. Discovery results may miss the most recent signals until sync resumes.",
  },
  unknown: {
    dot: "bg-gray-400",
    label: () => "Connectors: checking…",
    tooltip: "Checking connector health…",
  },
};

function ConnectorHealthDot() {
  const { data } = useConnectorHealth();
  const state = data?.state ?? "unknown";
  const style = STATE_STYLES[state];
  // Show only when something is actually wrong (degraded). The "stale"
  // and "healthy" states aren't worth painting in the sidebar — they
  // either confused viewers ("is the product broken?") or were noise.
  if (state !== "degraded") return null;
  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-sidebar-foreground/65"
      role="status"
      aria-live="polite"
      title={style.tooltip}
    >
      <span className={cn("inline-block size-2 rounded-full", style.dot)} />
      {style.label(data?.total ?? 0, data?.degraded ?? 0)}
    </div>
  );
}

/* ─── shared pieces ─── */

function BrandMark() {
  return (
    <Link
      href="/dashboard"
      className="inline-flex items-baseline rounded-md focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none"
      aria-label="Thexis — dashboard"
    >
      <span className="font-serif text-[26px] font-normal leading-none tracking-tight text-sidebar-foreground">
        thexis
      </span>
    </Link>
  );
}

function NavItems({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-1">
      {NAV_GROUPS.map((group) => (
        <div key={group.id} className={group.label ? "pt-3" : ""}>
          {group.label && (
            <div
              role="presentation"
              className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50"
            >
              {group.label}
            </div>
          )}
          {group.items.map((item) => {
            const active = isActive(pathname, item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex min-h-[44px] items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                  "motion-reduce:transition-none",
                  active
                    ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function SidebarFooter({ user, signOut, theme, toggleTheme, initials }: {
  user: UserLike | null;
  signOut: ReturnType<typeof useAuth>["signOut"];
  theme: "light" | "dark";
  toggleTheme: () => void;
  initials: string;
}) {
  return (
    <div className="border-t border-sidebar-border p-3">
      <ConnectorHealthDot />

      <div className="mt-1 flex items-center gap-1">
        {user && <NotificationDropdown />}
        <button
          onClick={toggleTheme}
          className="flex size-11 items-center justify-center rounded-md text-sidebar-foreground/65 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          aria-label={
            theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
          }
        >
          {theme === "dark" ? (
            <Sun className="size-4" />
          ) : (
            <Moon className="size-4" />
          )}
        </button>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-2 hover:bg-sidebar-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-foreground">
            {initials}
          </div>
          <div className="min-w-0 text-left">
            <div className="truncate text-[12px] text-sidebar-foreground">
              {getDisplayName(user)}
            </div>
            <div className="truncate text-[10px] text-sidebar-foreground/55">
              {user?.email ?? ""}
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {user?.email && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {user.email}
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()}>
            <LogOut className="mr-2 size-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/* ─── main component ─── */

export function Sidebar() {
  const pathname = usePathname();
  const { theme, toggleTheme } = useTheme();
  const { user: rawUser, signOut } = useAuth();
  const user = asUserLike(rawUser);
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = getUserInitials(user);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <>
      {/* Desktop rail (≥ md) */}
      <aside
        className="hidden w-[240px] shrink-0 flex-col border-r border-sidebar-border bg-[var(--nav-bg)] md:flex md:h-screen md:sticky md:top-0"
        aria-label="Primary"
      >
        <div className="px-4 py-5">
          <BrandMark />
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-3">
          <NavItems pathname={pathname} />
        </nav>
        <SidebarFooter
          user={user}
          signOut={signOut}
          theme={theme}
          toggleTheme={toggleTheme}
          initials={initials}
        />
      </aside>

      {/* Mobile top bar (< md) */}
      <div className="flex h-14 items-center justify-between border-b border-sidebar-border bg-[var(--nav-bg)] px-4 md:hidden">
        <BrandMark />
        <div className="flex items-center gap-1">
          {user && <NotificationDropdown />}
          <button
            onClick={() => setMobileOpen(true)}
            className="flex size-11 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer (< md, rendered to body via Dialog portal) */}
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/40 transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 motion-reduce:transition-none md:hidden" />
          <Dialog.Popup
            className={cn(
              "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-[var(--nav-bg)] shadow-xl md:hidden",
              "transition-transform duration-[220ms] ease-out",
              "data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full",
              "motion-reduce:transition-none",
            )}
            aria-label="Primary navigation"
          >
            <div className="flex items-center justify-between border-b border-sidebar-border px-4 py-4">
              <BrandMark />
              <Dialog.Close
                className="flex size-11 items-center justify-center rounded-md text-sidebar-foreground/65 hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
                aria-label="Close menu"
              >
                <X className="size-5" />
              </Dialog.Close>
            </div>
            <nav
              className="flex-1 overflow-y-auto px-2 py-3"
              aria-label="Primary"
            >
              <NavItems
                pathname={pathname}
                onNavigate={() => setMobileOpen(false)}
              />
            </nav>
            <SidebarFooter
              user={user}
              signOut={signOut}
              theme={theme}
              toggleTheme={toggleTheme}
              initials={initials}
            />
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
